import React, { useMemo, useEffect, useState } from 'react';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import EmailTagInput from '@mysimpledb/shared/components/UI/EmailTagInput';

/**
 * Props:
 *  - data: {
 *      sharedName, shareProfilePicture, sharing, viewing, sharedEmails
 *    }
 *  - types: [{id,key,name,short_label, subtypes:[{id,key,name,short_label}]}]
 *  - initialTypeIds: number[]
 *  - initialSubtypeIds: number[]
 *  - errors, loading
 *  - onFieldChange(field, value)
 *  - onSave({ allowedTypeIds, allowedSubtypeIds })
 */
const SharingSettings = ({
  data,
  types = [],
  initialTypeIds = [],
  initialSubtypeIds = [],
  errors = {},
  loading = false,
  onFieldChange,
  onSave,
}) => {
  // Local selection state for filters
  const [typeIds, setTypeIds] = useState(new Set(initialTypeIds));
  const [subtypeIds, setSubtypeIds] = useState(new Set(initialSubtypeIds));

  useEffect(() => {
    setTypeIds(new Set((initialTypeIds || []).map((n) => Number(n))));
  }, [initialTypeIds]);

  useEffect(() => {
    setSubtypeIds(new Set((initialSubtypeIds || []).map((n) => Number(n))));
  }, [initialSubtypeIds]);

  // Helpers
  const toggleType = (id) => {
    const typeId = Number(id);
    if (!Number.isFinite(typeId) || typeId <= 0) return;

    const t = types.find((x) => Number(x.id) === typeId);
    const subIds = (t?.subtypes || [])
      .map((s) => Number(s.id))
      .filter(Number.isFinite);

    const nextTypes = new Set(typeIds);
    const nextSub = new Set(subtypeIds);

    if (nextTypes.has(typeId)) {
      // turning OFF type => turn off all subtypes
      nextTypes.delete(typeId);
      subIds.forEach((sid) => nextSub.delete(sid));
    } else {
      // turning ON type => turn on all subtypes
      nextTypes.add(typeId);
      subIds.forEach((sid) => nextSub.add(sid));
    }

    setTypeIds(nextTypes);
    setSubtypeIds(nextSub);
  };

  const toggleSubtype = (typeId, subId) => {
    const st = new Set(subtypeIds);
    if (st.has(subId)) st.delete(subId);
    else {
      st.add(subId);
      // ensure parent type on
      if (!typeIds.has(typeId)) {
        const nextTypes = new Set(typeIds);
        nextTypes.add(typeId);
        setTypeIds(nextTypes);
      }
    }
    setSubtypeIds(st);
  };

  const selectAllInType = (typeId, checked) => {
    const t = types.find((x) => x.id === typeId);
    const nextTypes = new Set(typeIds);
    const nextSub = new Set(subtypeIds);

    if (checked) {
      nextTypes.add(typeId);
      t?.subtypes?.forEach((s) => nextSub.add(s.id));
    } else {
      nextTypes.delete(typeId);
      t?.subtypes?.forEach((s) => nextSub.delete(s.id));
    }
    setTypeIds(nextTypes);
    setSubtypeIds(nextSub);
  };

  const handleSave = () => {
    onSave?.({
      allowedTypeIds: Array.from(typeIds),
      allowedSubtypeIds: Array.from(subtypeIds),
    });
  };

  const emailChips = useMemo(
    () => (data.sharedEmails || []).map((e) => e.trim()).filter(Boolean),
    [data.sharedEmails]
  );

  return (
    <div className="sharing-container">
      {/* SHARING OPTIONS */}
      <div className="section">
        <h2>Sharing options</h2>

        <label>Shared name (15 chars max)</label>
        <input
          className={`stylish-input2 ${errors.sharedName ? 'input-error' : ''}`}
          maxLength={15}
          style={{ width: '25ch' }}
          value={data.sharedName}
          onChange={(e) => onFieldChange('sharedName', e.target.value)}
        />
        {errors.sharedName ? (
          <div className="error-text">{errors.sharedName}</div>
        ) : null}

        <div style={{ marginTop: 16 }}>
          <label>
            <input
              type="checkbox"
              checked={!!data.shareProfilePicture}
              onChange={(e) =>
                onFieldChange('shareProfilePicture', e.target.checked)
              }
            />{' '}
            Share profile picture
          </label>
        </div>

        <div style={{ marginTop: 16 }}>
          <label>Who can see your public records?</label>
          <ul className="checkbox-list">
            <li>
              <label>
                <input
                  type="radio"
                  name="share-scope"
                  value="friends"
                  checked={data.sharing === 'friends'}
                  onChange={() => onFieldChange('sharing', 'friends')}
                />{' '}
                Share my public records with friends
              </label>
            </li>
            <li>
              <label>
                <input
                  type="radio"
                  name="share-scope"
                  value="everyone"
                  checked={data.sharing === 'everyone'}
                  onChange={() => onFieldChange('sharing', 'everyone')}
                />{' '}
                Share my public records with everyone
              </label>
            </li>
            <li>
              <label>
                <input
                  type="radio"
                  name="share-scope"
                  value="none"
                  checked={data.sharing === 'none'}
                  onChange={() => onFieldChange('sharing', 'none')}
                />{' '}
                Do not share any of my records (default)
              </label>
            </li>
          </ul>
        </div>
      </div>

      {/* VIEWING OPTIONS */}
      <div className="section">
        <h2>Viewing options</h2>
        <ul className="checkbox-list">
          <li>
            <label>
              <input
                type="radio"
                name="view-scope"
                value="friends"
                checked={data.viewing === 'friends'}
                onChange={() => onFieldChange('viewing', 'friends')}
              />{' '}
              View my friends’ public records
            </label>
          </li>
          <li>
            <label>
              <input
                type="radio"
                name="view-scope"
                value="all"
                checked={data.viewing === 'all'}
                onChange={() => onFieldChange('viewing', 'all')}
              />{' '}
              View all public records
            </label>
          </li>
          <li>
            <label>
              <input
                type="radio"
                name="view-scope"
                value="none"
                checked={data.viewing === 'none'}
                onChange={() => onFieldChange('viewing', 'none')}
              />{' '}
              Do not view any public records (default)
            </label>
          </li>
        </ul>
      </div>

      {/* USER TYPE FILTERS */}
      <div className="section">
        <h2>User type filters</h2>
        <p style={{ marginTop: 4, marginBottom: 12 }}>
          Only show shared entries from the selected types (and subtypes). Leave
          all unchecked to see everything that matches your viewing option.
        </p>

        {types.map((t) => {
          const allSubIds = (t.subtypes || []).map((s) => s.id);
          const allSelected =
            allSubIds.length > 0 && allSubIds.every((id) => subtypeIds.has(id));
          const someSelected =
            allSubIds.some((id) => subtypeIds.has(id)) && !allSelected;

          return (
            <div key={t.id} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontWeight: 800, marginTop: '15px' }}>
                  <input
                    type="checkbox"
                    checked={typeIds.has(t.id)}
                    onChange={(e) => toggleType(t.id)}
                  />{' '}
                  {t.name}
                </label>

                {t.subtypes?.length ? (
                  <label
                    style={{ fontSize: 12, opacity: 0.9, marginTop: '15px' }}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={(e) => selectAllInType(t.id, e.target.checked)}
                    />{' '}
                    all subtypes
                  </label>
                ) : null}
              </div>

              {t.subtypes?.length ? (
                <div
                  style={{
                    marginLeft: 28,
                    marginTop: 6,
                    display: 'grid',
                    gridTemplateColumns:
                      'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 6,
                  }}
                >
                  {t.subtypes.map((s) => (
                    <label key={s.id}>
                      <input
                        type="checkbox"
                        checked={subtypeIds.has(s.id)}
                        onChange={() => toggleSubtype(t.id, s.id)}
                      />{' '}
                      {s.name}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* FRIENDS TO SHARE WITH */}
      <div className="section">
        <h2>Friends to share with</h2>
        <EmailTagInput
          emails={data.sharedEmails}
          setEmails={(list) => onFieldChange('sharedEmails', list)}
        />
      </div>

      <div className="sharing-save">
        <AppButton
          variant="solid"
          disabled={loading}
          onPress={handleSave}
          style={{ width: 150 }}
        >
          {loading ? 'Saving…' : 'Save'}
        </AppButton>
      </div>
    </div>
  );
};

export default SharingSettings;
